import { useState } from 'react';
import { useTranslation } from '../../lib/i18n';
import {
  askCardLines,
  maestroActionSentence,
  workingSentence,
  type MaestroActionRow,
  type OutreachContext,
  type QuickViewRow,
  type QuickViewTask,
} from '../../lib/island-quickview';
import { avatarInitials, avatarSlotFor } from '../../lib/avatar-color';
import { cardTapOpensTask } from '../../lib/island-card-tap';
import { channelLabelKey } from '../../lib/contact-rows';
import { timeAgo } from '../../lib/notification-time';
import type { WebOpenAsk } from '../../lib/open-asks';
import { messageDir } from '../../lib/message-direction';
import { raisedDay } from '../../lib/task-decision-body';
import { renderDayLabel } from '../../lib/outreach-thread-view-render';
import { AgentDot } from '../AgentDot';
import { MaestroMark } from './MaestroMark';
import { Avatar, Badge, TextButton } from './ui';
import { TaskDecisionPrompts } from './tasks/TaskDecisionPrompts';
import { SocialPostPreviewCard } from './chat-surface/MessageRow/SocialPostPreviewCard';
import { postPreviewHoldsDecision } from '../../lib/task-row-post-preview';
import styles from './AgentsIslandQuickViewRow.module.css';

/** BI for this card — no `page_version` in use on this surface (every `logPageClickBi` call here
 *  already omits it), so the decision block gets the same bare `page_name`. */
const ISLAND_PAGE = { page_name: 'agents-island-quickview' };

// ONE row of the agents-island quick-view. Its own file because the panel around it drags in the task
// modals (and, through them, the markdown renderer), and the row's affordance — the part a reader has to
// be able to tell apart at a glance — is worth being able to render on its own in a test.

const INK = 'var(--sym-ink)';
const INK_2 = 'var(--ds-text-secondary)';

const META_LINE: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '-0.24px',
  color: INK_2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};


/** The row's lead line (title / question / outreach subject): 14/18, primary, one ellipsized line. Weight
 *  is Medium for a task/decision and SemiBold for an outreach subject (Figma). */
const LEAD_LINE: React.CSSProperties = {
  fontSize: 14,
  lineHeight: '18px',
  letterSpacing: '-0.42px',
  color: INK,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** A waiting title wraps to TWO lines before it ellipsizes, where every other lead line on this card is
 *  one `nowrap` line. That is the whole point of the list: sibling tasks from one plan are routinely
 *  identical up to their last word ("…invitation to friend 1 / 2 / 3"), so a single ellipsized line
 *  renders three visually identical rows — which is the duplicate-looking-rows report this card was
 *  built to answer, reproduced inside the answer. Caught by rendering it, not by a test. Shared 1:1 with
 *  iOS `lineLimit(2)` and Android `maxLines = 2`. */
const WAITING_TITLE_LINE: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 'var(--sym-weight-medium)',
  lineHeight: '18px',
  letterSpacing: '-0.42px',
  color: INK,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};

/** How many task titles a question card lists before it folds the rest into "+{n} more". Three, like the
 *  card's own row cap — a question blocking a twelve-task plan must not turn one card into the list it
 *  exists to replace. */
const WAITING_TITLES_SHOWN = 3;

/** The trailing origin word on a task row's meta line — "Routine" / "Goal" (Tiny/SemiBold, PRIMARY ink). */
const ORIGIN_LABEL: React.CSSProperties = {
  fontSize: 12,
  lineHeight: '16px',
  fontWeight: 'var(--sym-weight-semibold)',
  letterSpacing: '-0.24px',
  color: INK,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

/** ONE row of the orange "action needed" card (Figma 17339-142828 / 17666-231716). META-first (like the
 *  blue card), with two shapes — a TASK (from a routine/goal) or a DECISION (answer chips). No status badge
 *  and no origin FOLD anymore: each action-required task is its own row, carrying a small "Routine"/"Goal"
 *  origin word on its meta line. A question raised on an OUTREACH thread also names its person, channel
 *  and thread, with "Open conversation" (`outreach`). Tapping the card outside its controls opens the task.
 *  Exported for the behavior tests. */
export function QuickViewRowButton({
  task,
  agentName,
  onOpen,
  outreach,
  onOpenConversation,
  onOpenPlan,
  page = ISLAND_PAGE,
}: {
  task: QuickViewRow;
  /** A plan question's "See the full plan": the island closes before the goal opens. */
  onOpenPlan?: () => void;
  agentName?: string;
  onOpen: (task: QuickViewRow) => void;
  /** The outreach a question card's prompt was raised on (`outreachContext`), when the join resolved. */
  outreach?: OutreachContext;
  /** "Open conversation" — the thread the question is about. */
  onOpenConversation?: (outreach: OutreachContext) => void;
  /** The BI page the answer block reports under. The island by default. */
  page?: { page_name: string; page_version?: string };
}) {
  const { t } = useTranslation();
  // Only a signal to re-render after a decision lands (the row re-reads from cache); no local flag is read
  // since the status badge is gone.
  const [, setAnsweredHere] = useState(false);
  // The questions answered on this card, so a held post leaves with its question instead of waiting for the re-read.
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const onAnswered = (allAnswered: boolean, decisionId: string) => {
    setAnsweredHere(allAnswered);
    setAnsweredIds((ids) => (ids.includes(decisionId) ? ids : [...ids, decisionId]));
  };

  const rowButton: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12, // meta line ↔ body (Figma spacing/12)
    padding: 0, // flush to the card's 20px content inset; the 14 row rhythm rides the container gap
    borderRadius: 12,
    textAlign: 'left',
    width: '100%',
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    cursor: 'pointer',
    color: INK,
  };

  // TASK / DECISION — "{Agent} | {Workspace}" on the meta line + a right-aligned "Routine"/"Goal" origin
  // word when the task has one; the title (Medium) as the lead; a goal task also gets a subtitle; and the
  // answer chips, when a decision is open, below the row (a button can't nest the chip buttons).
  //
  // A QUESTION CARD (`task.question`) is the same anatomy with its body rearranged: one question, the
  // chips that answer it, and then the TITLES of every task waiting on that answer. Its lead title is
  // dropped, because the card stands for N tasks and that list names them all — printing one of them
  // above the question is the "three identical-looking rows" defect in miniature, and printing the
  // representative's title twice (once as the lead, once in the list) is just noise. The card says what
  // is waiting and nothing about what answering will release: the branch a task belongs to
  // (`DecisionBlocker.requiredChoice`) never reaches the client, so "releases 3 tasks" would be unknowable.
  const question = task.question;
  const identity =
    agentName && task.workspaceName ? `${agentName} | ${task.workspaceName}` : agentName || task.workspaceName || '';
  const originLabel = task.pulseIdentityId
    ? t('agentsIsland.quickView.originRoutine')
    : task.goalId
    ? t('agentsIsland.quickView.originGoal')
    : null;
  const subtitle = task.goalId ? task.summary?.trim() : undefined;
  // TAP (outreach-needs-you contract rule 4): tapping the card ANYWHERE outside its chips, reply box and links opens
  // THAT task, the same drill-in the Tasks screen's row opens — and `onOpen` closes the panel first. The row button
  // only covers the meta line and title, and a QUESTION card draws its question, chips and "Waiting on this answer"
  // list OUTSIDE it (a button cannot nest the chip buttons), so a tap on the question used to land on nothing at
  // all. The card therefore takes the click itself and ignores one that landed on a control, which already does its
  // own thing — the DS Card's nested-control rule (`e.target.closest`), not a `stopPropagation` on every control.
  // No role or tabIndex on the wrapper: the row button is the keyboard and screen-reader path to the same action.
  // The social post the agent holds for the owner's OK, in its COMPACT form above the question — users approve
  // posts from here, so they must see the post. Only over the question this card answers: a question card
  // answers its one prompt, so a post held on another question of the same task stays off it. The compact
  // form has no control of its own, so a tap on it is the card's tap (`openFromCard`) and opens the task; and
  // it is part of the card the fit rule measures, so 3-vs-2 still decides on whole cards.
  const postPreview = postPreviewHoldsDecision(task.socialPostPreviewCard, question ? [question.prompt] : task.decisionPrompts, answeredIds);
  const openFromCard = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cardTapOpensTask(event.target as HTMLElement | null)) onOpen(task);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, cursor: 'pointer' }} onClick={openFromCard} data-island-card="task">
      <button
        type="button"
        onClick={() => onOpen(task)}
        style={rowButton}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
            {/* The Maestro is deliberately OUT of the agent colour system (see the blue row above and
                FileCreatedBy): an unattributed row (no `agentId`) is the team lead's, so it wears the
                MaestroMark, never a blank slot. */}
            {task.agentId ? <AgentDot id={task.agentId} size={12} /> : <MaestroMark size={12} />}
            {identity ? <span style={META_LINE}>{identity}</span> : null}
          </span>
          {originLabel ? <span style={ORIGIN_LABEL}>{originLabel}</span> : null}
        </span>
        {/* A question card drops the lead title — its own body names every task waiting on the
            answer, and one of those titles printed above the question is where the duplicate-looking
            rows came from. */}
        {question ? null : (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            {/* Figma 17339-142828: a GOAL title reads SemiBold 14/18 (like the outreach subject). A
                ROUTINE title (a routine/pulse row) also reads SemiBold 14/18; a plain decision title
                stays Medium 14/18. */}
            <span
              style={{
                ...LEAD_LINE,
                ...(task.goalId && !task.pulseIdentityId
                  ? { fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.28px' }
                  : task.pulseIdentityId
                  ? { fontWeight: 'var(--sym-weight-semibold)' }
                  : { fontWeight: 'var(--sym-weight-medium)' }),
              }}
            >
              {task.title || t('tasks.untitledTask')}
            </span>
            {subtitle ? (
              <span style={{ fontSize: 14, lineHeight: '20px', letterSpacing: '-0.28px', color: INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {subtitle}
              </span>
            ) : null}
          </span>
        )}
      </button>
      {/* The question this row is held on, answered in place. Rendered unconditionally — it draws the answer
          chips for a blocked task, the "unavailable" line when the prompt could not be read, and NOTHING
          otherwise (so most action-required rows show no extra). Outside the button — a button can't nest
          the answer buttons. `origin={null}`: no goal title travels with the row, so no unresolvable link.

          A question card answers `question.prompt` ALONE and not everything its representative task holds:
          a task blocked on two questions sits under BOTH cards, and without the override each of those two
          cards would draw both questions. `taskCount` rides the card's own BI (`task_count`) — the events
          keep the representative task id, so the fan-out is readable from the count rather than inferred. */}
      {/* The outreach a question was raised on (`Decision.outreach_id`): the person on their channel, the
          thread, and the way to it. A sibling of the row button, never inside it. */}
      {question && outreach ? <OutreachQuestionContext outreach={outreach} onOpenConversation={onOpenConversation} /> : null}
      {postPreview ? <SocialPostPreviewCard card={postPreview} variant="compact" /> : null}
      <TaskDecisionPrompts
        task={task}
        prompts={question ? [question.prompt] : undefined}
        taskCount={question ? question.tasks.length : undefined}
        origin={null}
        onAnsweredChange={onAnswered}
        page={page}
        pillChips
        postAt={task.socialPostPreviewCard?.scheduledAt}
        onOpenPlan={onOpenPlan}
      />
      {question && question.tasks.length > 0 ? <WaitingOnThisAnswer tasks={question.tasks} /> : null}
    </div>
  );
}

/** An OPEN ASK on the orange card (contract rule 4) — a question the Maestro put to the reader that no task carries
 *  (a chat's own ask, a held send, a card, a goal or meeting question). Counted by the section's number, so drawn
 *  here too, with the Maestro (and the day it was raised) on the meta line.
 *
 *  A DECISION (`ask.prompt`) answers IN PLACE, the way the Action needed tab's `OpenAsk` does: the shared
 *  `TaskDecisionPrompts` block — question, the detail (a plan's steps, one per line) with "More about this", and
 *  Approve/Decline or the option chips. That card is NOT a button: a button cannot nest answer buttons, and a
 *  whole-card chat link is what misled — a goal's plan approval was never posted into the chat it was raised
 *  from, so the reader opened that chat and found no plan to approve. "Open conversation" stays under it only
 *  for a NON-goal ask (`!ask.goalId`), whose chat does hold the question. A tap anywhere outside the card's controls
 *  opens the ask's home (`askOpenTarget`): a goal question's GOAL, else its chat.
 *
 *  A ledger ask with no prompt (`conv:` / `card:` / `held-send:`) is what the push said, and the whole card
 *  opens the chat it was put in. Exported for the behavior tests. */
export function AskRowButton({
  ask,
  maestroName,
  onOpen,
  onOpenPlan,
}: {
  ask: WebOpenAsk;
  maestroName: string;
  onOpen: (ask: WebOpenAsk) => void;
  /** A plan question's "See the full plan": the island closes before the goal opens. */
  onOpenPlan?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const fallback = t('outreach.line.needsYouFallback', { maestroAgentName: maestroName });
  const { lead, body } = askCardLines(ask, fallback);
  // The day the ask was raised, beside the Maestro — a "tomorrow" in it is read against that day.
  const day = raisedDay([ask.prompt?.createdDate, ask.createdDate], new Date());
  const dayText = day ? renderDayLabel(day, t as unknown as (key: string, params?: Record<string, unknown>) => string, i18n.language || undefined) : '';
  const meta = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      <MaestroMark size={12} />
      <span style={META_LINE}>{dayText ? `${maestroName} · ${dayText}` : maestroName}</span>
    </span>
  );
  if (ask.prompt) {
    // The day shows ONCE, on the meta line: the block draws its own raised day from the prompt's `createdDate`
    // (or `raisedAt`), so the prompt goes in without it and no `raisedAt` is passed — as on Android.
    const { createdDate: _raised, ...undated } = ask.prompt as typeof ask.prompt & { createdDate?: string };
    void _raised;
    const prompt = undated.question?.trim() ? undated : { ...undated, question: fallback };
    // TAP (contract rule 4): a tap anywhere outside the card's own controls opens the ask's home — the goal for a goal
    // question, else the chat it was raised in — exactly as a task card opens its task. The card used to take no tap
    // at all, so a reader who tapped the question got nothing. Same nested-control guard as `QuickViewRowButton`.
    const openFromCard = (event: React.MouseEvent<HTMLDivElement>) => {
      if (cardTapOpensTask(event.target as HTMLElement | null)) onOpen(ask);
    };
    return (
      <div
        data-island-card="ask"
        onClick={openFromCard}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 12, minWidth: 0, color: INK, cursor: 'pointer' }}
      >
        {meta}
        {/* No task behind it (`id: ''`): the verdict carries none and the BI no `task_id`. A goal question's
            `goalId` is what the answer flow writes the verdict against. */}
        <TaskDecisionPrompts
          task={{ id: '', goalId: ask.goalId }}
          prompts={[prompt]}
          origin={null}
          page={ISLAND_PAGE}
          pillChips
          rowKey={ask.id}
          onOpenPlan={onOpenPlan}
        />
        {!ask.goalId ? (
          <span style={{ display: 'flex' }}>
            <TextButton size="small" underline onClick={() => onOpen(ask)}>
              {t('outreach.island.openConversation')}
            </TextButton>
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(ask)}
      data-island-card="ask"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12, // meta line ↔ body, as on a task card
        padding: 0,
        borderRadius: 12,
        textAlign: 'start',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      {meta}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        {/* Two lines, like a waiting title: a body that became the lead must not be cut to one. The whole
            card opens the chat the ask was put in, where it reads in full. */}
        <span style={WAITING_TITLE_LINE} dir={messageDir(lead)}>
          {lead}
        </span>
        {body ? (
          <span style={{ ...WAITING_TITLE_LINE, fontWeight: undefined, color: INK_2 }} dir={messageDir(body)}>
            {body}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/** The outreach context on a question card: Avatar + "{name} · {channel}" + the thread title + "Open conversation". */
function OutreachQuestionContext({
  outreach,
  onOpenConversation,
}: {
  outreach: OutreachContext;
  onOpenConversation?: (outreach: OutreachContext) => void;
}) {
  const { t } = useTranslation();
  const channelKey = channelLabelKey(outreach.channel);
  const channel = channelKey ? t(channelKey) : outreach.channel;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} data-outreach-context={outreach.outreachId}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Avatar size={20} initials={avatarInitials(outreach.name)} color={avatarSlotFor(outreach.name)} />
        <span style={META_LINE} dir="auto">
          {t('outreach.personOnChannel', { name: outreach.name, channel })}
        </span>
      </span>
      {outreach.title ? (
        <span style={{ ...LEAD_LINE, fontWeight: 'var(--sym-weight-semibold)', letterSpacing: '-0.28px' }} dir="auto">
          {outreach.title}
        </span>
      ) : null}
      {onOpenConversation ? <OpenConversationLink outreach={outreach} onOpenConversation={onOpenConversation} /> : null}
    </span>
  );
}

/** "Open conversation" — into the contact thread the question is about. */
function OpenConversationLink({
  outreach,
  onOpenConversation,
}: {
  outreach: OutreachContext;
  onOpenConversation: (outreach: OutreachContext) => void;
}) {
  const { t } = useTranslation();
  return (
    <span style={{ display: 'flex' }}>
      <TextButton size="small" underline onClick={() => onOpenConversation(outreach)}>
        {t('outreach.island.openConversation')}
      </TextButton>
    </span>
  );
}

/**
 * The tasks held on ONE answer, under "Waiting on this answer" — the body of a question card.
 *
 * Titles only, and deliberately not links or buttons: the card's affordance is answering the question, and
 * N tappable titles would be N ways to leave the one screen built to let the reader stay. Capped at
 * {@link WAITING_TITLES_SHOWN} with a "+{n} more" line, so a plan with twelve blocked tasks is still a card.
 */
function WaitingOnThisAnswer({ tasks }: { tasks: QuickViewTask[] }) {
  const { t } = useTranslation();
  const shown = tasks.slice(0, WAITING_TITLES_SHOWN);
  const more = Math.max(0, tasks.length - shown.length);
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }} data-waiting-on-answer={tasks.length}>
      <span style={META_LINE}>{t('agentsIsland.quickView.waitingOnThisAnswer')}</span>
      {shown.map((waiting) => (
        <span key={waiting.id} style={{ ...WAITING_TITLE_LINE }} dir="auto">
          {waiting.title || t('tasks.untitledTask')}
        </span>
      ))}
      {more > 0 ? <span style={META_LINE}>{t('agentsIsland.quickView.moreTasksOnAnswer', { count: more })}</span> : null}
    </span>
  );
}

/** The action sentence line — the row's LEAD (Figma "Frame 2147242241"): Wix Madefor Medium 14/18,
 *  Text&Icon/Primary. Wraps to TWO lines, then ellipsizes (per product — the frame draws one line, but a
 *  longer action reads better across two than truncated at one). Always primary ink, including the
 *  "Working on: {title}" fallback — the frame draws no muted case. */
const SENTENCE_LINE: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 'var(--sym-weight-medium)',
  lineHeight: '18px',
  letterSpacing: '-0.42px',
  color: INK,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};

/**
 * ONE row of the blue "agents at work" card (Figma 17339-98544). The META line leads and the action
 * leads visually — the inverse of the pre-redesign agent-first row:
 *
 *   - top META line: a small avatar wearing the synchronized HEARTBEAT (the pill's single-agent beat,
 *     scale 1→1.18→1 over 1.7s; every row in the card beats in unison — see `.beat`), then
 *     "{Agent} | {Workspace}" in the Tiny/SemiBold secondary ink, a REVIEWING badge when IN_REVIEW, and
 *     the compact elapsed ("2m") pushed to the trailing edge. A task with no `agentId` is the Maestro's,
 *     shown under the user's own (renameable) assistant name; a task with no workspace drops the "| …".
 *   - the ACTION sentence below, by the shared chain (`workingSentence`): `currentAction` → the first
 *     unticked plan step → the localized "Working on: {title}". No separate title line (the frame has none).
 *
 * Tap opens the task, exactly as a peek row does. Exported for the behavior tests.
 */
export function WorkingRowButton({
  task,
  agentLabel,
  onOpen,
}: {
  task: QuickViewTask;
  /** The resolved display name: the roster's name for `task.agentId`, or the Maestro's own. */
  agentLabel: string;
  onOpen: (task: QuickViewTask) => void;
}) {
  const { t } = useTranslation();
  const sentence = workingSentence(task);
  const title = task.title || t('tasks.untitledTask');
  const elapsed = timeAgo(task.lastActivityAt ?? null);
  const heroText = sentence.kind === 'title' ? t('agentsIsland.quickView.workingOn', { title }) : sentence.text;
  const identity = task.workspaceName ? `${agentLabel} | ${task.workspaceName}` : agentLabel;
  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      data-working-row
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12,
        padding: 0, // rows flush to the card's 20px content inset; the 14px row rhythm rides the container gap (Figma)
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
          <span className={styles.beat} aria-hidden>
            {/* The Maestro is deliberately OUT of the agent colour system (see FileCreatedBy) — an
                unattributed live run is the team lead's, never a hued dot pretending to be a teammate. */}
            {task.agentId ? <AgentDot id={task.agentId} size={14} /> : <MaestroMark size={14} />}
          </span>
          <span style={META_LINE}>{identity}</span>
          {task.status === 'IN_REVIEW' ? (
            <Badge variant="in-progress" label={t('agentsIsland.quickView.reviewing')} style={{ flexShrink: 0 }} />
          ) : null}
        </span>
        {elapsed ? <span style={{ ...META_LINE, flexShrink: 0 }}>{elapsed}</span> : null}
      </span>
      <span style={SENTENCE_LINE}>{heroText}</span>
    </button>
  );
}

/**
 * ONE Maestro-ACTION row of the blue card — the Maestro personally at work on something that is not a
 * task (a Wixel render, a free-run agent loop). Same anatomy as a working row: the Maestro's mark in the
 * synchronized heartbeat, the user's own (renameable) assistant name on the meta line, compact elapsed
 * from `startedAt` — but no "| {workspace}" (a maestro action has no workspace), no reviewing badge, and
 * the action `label` (else a localized per-kind fallback) as the lead sentence. An empty label falls
 * back rather than rendering an empty line.
 *
 * Tap opens the MAIN CHAT (the surface's existing open-chat navigation): the action lives on the maestro
 * conversation, so the chat is where its progress and outcome will land. A job an AGENT dispatched wears
 * that agent's dot and name, and the caller opens its task instead (`action.taskId`) — usually that job
 * renders as its task's own row and never reaches here; this is the case where the task is not loaded.
 * Exported for the behavior tests.
 */
export function MaestroActionRowButton({
  action,
  agentLabel,
  onOpen,
}: {
  action: MaestroActionRow;
  /** The dispatching agent's roster name, else the Maestro's — the same source the rest of the chrome uses. */
  agentLabel: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const sentence = maestroActionSentence(action);
  const elapsed = timeAgo(action.startedAt ?? null);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-maestro-action-row={action.kind}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 12,
        padding: 0, // rows flush to the card's 20px content inset; the 14px row rhythm rides the container gap (Figma)
        borderRadius: 12,
        textAlign: 'left',
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
          <span className={styles.beat} aria-hidden>
            {action.agentId ? <AgentDot id={action.agentId} size={14} /> : <MaestroMark size={14} />}
          </span>
          <span style={META_LINE}>{agentLabel}</span>
        </span>
        {elapsed ? <span style={{ ...META_LINE, flexShrink: 0 }}>{elapsed}</span> : null}
      </span>
      <span style={SENTENCE_LINE}>{sentence.kind === 'label' ? sentence.text : t(sentence.key)}</span>
    </button>
  );
}

/**
 * ONE row of the orange card's People section (`native-apps/shared/outreach-needs-you/contract.json` rule 4): the
 * contact's Avatar (photo, else initials — the shared DS avatar), their name (the Inbox row's fallback chain), and
 * ONE line of the open question (`needsYou.question`, else `outreach.line.needsYouFallback`). Tapping it opens THAT
 * thread, the route the Inbox row opens. No chips and no reply box: the thread is where it is answered.
 * Exported for the behavior tests.
 */
export function PeopleWaitingRowButton({
  name,
  photoUrl,
  colorSeed,
  question,
  onOpen,
}: {
  name: string;
  photoUrl?: string;
  /** What picks the avatar's ramp slot — the person (or recipient) id, as the Inbox row seeds it. */
  colorSeed: string;
  question: string;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-island-person
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 0,
        width: '100%',
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        textAlign: 'start',
        cursor: 'pointer',
        color: INK,
      }}
    >
      <Avatar size={32} initials={avatarInitials(name)} photoUrl={photoUrl} color={avatarSlotFor(colorSeed || name)} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ ...LEAD_LINE, fontWeight: 'var(--sym-weight-semibold)' }} dir="auto">
          {name}
        </span>
        <span style={{ ...LEAD_LINE, color: INK_2 }} dir="auto">
          {question}
        </span>
      </span>
    </button>
  );
}
